import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import { FcmDataModel, DeleteFcmDataModel } from '@/shared/models/fcm-data.model';
import * as fcmData from '@/shared/services/data/user-data/fcm.data';
import {
  getFCMToken,
  initializeMessaging
} from '@/shared/services/utils/firebase/messaging.service';

/**
 * Save the FCM token for user to server
 * @param {string} token - The FCM token
 * @param {string} userId - Optional user ID
 */
export const saveFCMTokenToServer = async (token: string, userId?: string): Promise<void> => {
  try {
    const fcmTokenData: FcmDataModel = {
      token,
      platform: 'internal_admin',
      client: 'web'
    };

    const result = await fcmData.updateClientFcmToken(fcmTokenData, 'notification');

    if (result === true) {
      console.log('FCM token registered successfully');
    } else if (isErrorInfo(result)) {
      console.error('Failed to register FCM token:', result);
    }
  } catch (error) {
    console.error('Error saving FCM token to server:', error);
  }
};

/**
 * Register device for push notifications after user login
 * Call this function after successful authentication
 * @param {string} userId - The user ID
 * @deprecated Use the singleton messaging service instead via FirebaseMessagingInitializer component
 */
export const registerDeviceForNotifications = async (userId?: string): Promise<void> => {
  console.warn(
    'registerDeviceForNotifications is deprecated. Use FirebaseMessagingInitializer component instead.'
  );
  // The singleton messaging service handles all initialization and token registration
  // This function is kept for backward compatibility but is no longer needed
};

/**
 * Unregister device from notifications (on logout)
 */
export const unregisterDeviceFromNotifications = async (): Promise<void> => {
  try {
    const token = localStorage.getItem('fcm_token');

    if (token) {
      // Unregister from server
      const fcmTokenData: DeleteFcmDataModel = { platform: 'internal_admin', client: 'web' };
      const result = await fcmData.deleteClientFcmToken(fcmTokenData, 'NOTIFICATION_MODULE');

      if (result === true) {
        console.log('FCM token unregistered successfully');
      } else if (isErrorInfo(result)) {
        console.error('Failed to unregister FCM token:', result);
      }
    }

    // Clean up local storage
    localStorage.removeItem('fcm_token');
    localStorage.removeItem('fcm_token_timestamp');
    localStorage.removeItem('fcm_token_registered');
  } catch (error) {
    console.error('Error unregistering device from notifications:', error);
  }
};

/**
 * Check if we have a valid FCM token
 */
export const hasValidFCMToken = (): boolean => {
  const token = localStorage.getItem('fcm_token');
  const timestamp = localStorage.getItem('fcm_token_timestamp');

  if (!token || !timestamp) {
    return false;
  }

  // Check if token is older than 24 hours (optional refresh logic)
  const tokenAge = Date.now() - parseInt(timestamp);
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours

  return tokenAge < maxAge;
};

/**
 * Initialize notifications system
 * Should be called on app startup
 * @deprecated Use FirebaseMessagingInitializer component instead
 */
export const initializeNotifications = async (): Promise<void> => {
  console.warn(
    'initializeNotifications is deprecated. Use FirebaseMessagingInitializer component instead.'
  );

  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    // Register service worker if not already registered
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register(
          (import.meta.env.BASE_URL === '/'
            ? ''
            : (import.meta.env.BASE_URL || '').replace(/\/+$/, '')) + '/firebase-messaging-sw.js',
          {
            scope: '/firebase-cloud-messaging-push-scope'
          }
        );
        console.log('Service Worker registered successfully:', registration);

        // Prepare environment data for service worker
        const envData = {
          PUSH_NOTIFICATION_SCHEME: import.meta.env.VITE_PUSH_NOTIFICATION_SCHEME,
          PUSH_NOTIFICATION_SCHEME_FALLBACK: import.meta.env.VITE_PUSH_NOTIFICATION_SCHEME_FALLBACK,
          PUBLIC_URL:
            import.meta.env.BASE_URL === '/'
              ? ''
              : (import.meta.env.BASE_URL || '').replace(/\/+$/, ''),
          FIREBASE_CONFIG: {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: import.meta.env.VITE_FIREBASE_APP_ID,
            measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
          }
        };

        console.log('[notifications.service] Preparing environment data for service worker:', {
          PUSH_NOTIFICATION_SCHEME: envData.PUSH_NOTIFICATION_SCHEME,
          PUSH_NOTIFICATION_SCHEME_FALLBACK: envData.PUSH_NOTIFICATION_SCHEME_FALLBACK,
          PUBLIC_URL:
            import.meta.env.BASE_URL === '/'
              ? ''
              : (import.meta.env.BASE_URL || '').replace(/\/+$/, '')
        });

        // Function to send environment data to service worker
        const sendEnvToServiceWorker = (worker: ServiceWorker | null) => {
          if (worker) {
            worker.postMessage({
              type: 'SET_ENV_VARS',
              data: envData
            });
            console.log('[notifications.service] Environment data sent to service worker');
          }
        };

        // Send immediately if service worker is active
        if (registration.active) {
          sendEnvToServiceWorker(registration.active);
        }

        // Send when service worker becomes active (for first install)
        if (registration.installing) {
          registration.installing.addEventListener('statechange', (event) => {
            const serviceWorker = event.target as ServiceWorker;
            if (serviceWorker?.state === 'activated') {
              sendEnvToServiceWorker(registration.active);
            }
          });
        }

        // Also send when controller changes
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (navigator.serviceWorker.controller) {
            sendEnvToServiceWorker(navigator.serviceWorker.controller);
          }
        });
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }

    // Note: Firebase messaging initialization is now handled by the singleton service
    // No need to call initializeMessaging here as it's handled by FirebaseMessagingInitializer
  } catch (error) {
    console.error('Error initializing notifications:', error);
  }
};
